/** Groups listing-level matches into one entry per buyer request, best fit first. */
export interface DemandMatchLike {
  request_id: string; matched_at: string; contacted: boolean;
  buyer_price_per_g: number | null; price_per_gram: number | null;
  min_thc_percent: number | null; thc_percent: number | null;
  min_quantity_g: number | null; quantity_grams: number | null;
}

/** Higher is better: price headroom, THC above floor, enough quantity. */
export function fitScore(m: DemandMatchLike): number {
  let s = 0;
  if (m.buyer_price_per_g != null && m.price_per_gram != null) {
    s += m.price_per_gram <= m.buyer_price_per_g ? 3 + (m.buyer_price_per_g - m.price_per_gram) * 10 : -2;
  } else if (m.price_per_gram != null) s += 1;
  if (m.min_thc_percent != null && m.thc_percent != null) s += m.thc_percent >= m.min_thc_percent ? 2 : -2;
  if (m.min_quantity_g != null && m.quantity_grams != null) s += m.quantity_grams >= m.min_quantity_g ? 2 : -1;
  return s;
}

export interface DemandGroup<T extends DemandMatchLike> {
  request_id: string; matches: T[]; latest: string; contacted: boolean;
}

export function groupDemand<T extends DemandMatchLike>(items: T[]): DemandGroup<T>[] {
  const map = new Map<string, T[]>();
  for (const it of items) map.set(it.request_id, [...(map.get(it.request_id) ?? []), it]);
  return [...map.entries()]
    .map(([request_id, ms]) => {
      const matches = [...ms].sort((a, b) => fitScore(b) - fitScore(a));
      return {
        request_id, matches,
        latest: ms.reduce((acc, m) => (m.matched_at > acc ? m.matched_at : acc), ms[0].matched_at),
        contacted: ms.some((m) => m.contacted),
      };
    })
    // Specific requests (few strong fits) surface before broad ones that match everything.
    .sort((a, b) => Number(a.contacted) - Number(b.contacted) || (b.latest > a.latest ? 1 : b.latest < a.latest ? -1 : 0));
}

/** Side-by-side "what they asked vs what you have" for one match. */
export type FitStatus = 'meets' | 'short' | 'open';
export interface FitCheck {
  price: FitStatus; thc: FitStatus; quantity: FitStatus;
  /** Buyer ceiling minus your price, per gram; positive = room to earn. Null when either side is missing. */
  headroomPerG: number | null;
}
export function fitCheck(m: DemandMatchLike): FitCheck {
  const cmp = (ask: number | null, have: number | null, higherIsBetter: boolean): FitStatus =>
    ask == null || have == null ? 'open' : (higherIsBetter ? have >= ask : have <= ask) ? 'meets' : 'short';
  return {
    price: cmp(m.buyer_price_per_g, m.price_per_gram, false),
    thc: cmp(m.min_thc_percent, m.thc_percent, true),
    quantity: cmp(m.min_quantity_g, m.quantity_grams, true),
    headroomPerG: m.buyer_price_per_g != null && m.price_per_gram != null ? m.buyer_price_per_g - m.price_per_gram : null,
  };
}

/** "Most room to earn" order: biggest positive headroom on the best fit first; unknown price last. */
export function sortByHeadroom<T extends DemandMatchLike>(groups: DemandGroup<T>[]): DemandGroup<T>[] {
  const h = (g: DemandGroup<T>) => fitCheck(g.matches[0]).headroomPerG ?? Number.NEGATIVE_INFINITY;
  return [...groups].sort((a, b) => h(b) - h(a));
}

/** "Best fit" order: strongest best-fit listing first; ties go to the newest request. */
export function sortByBestFit<T extends DemandMatchLike>(groups: DemandGroup<T>[]): DemandGroup<T>[] {
  return [...groups].sort((a, b) => fitScore(b.matches[0]) - fitScore(a.matches[0]) || (b.latest > a.latest ? 1 : b.latest < a.latest ? -1 : 0));
}

/** "Needed soonest" order: earliest deadline first; requests with no deadline last. */
export function sortBySoonest<T extends DemandMatchLike & { needed_by?: string | null }>(groups: DemandGroup<T>[]): DemandGroup<T>[] {
  const d = (g: DemandGroup<T>) => (g.matches[0].needed_by ? Date.parse(g.matches[0].needed_by) : Number.POSITIVE_INFINITY);
  return [...groups].sort((a, b) => d(a) - d(b));
}
